
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function AtlanticBeachEstatePage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = "Atlantic Beach Estate Painters - Professional Painting Services Cape Town";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services for Atlantic Beach Estate luxury coastal properties. Expert interior, exterior, and marine-grade coatings. Get your free quote today.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Atlantic Beach Estate painters, luxury coastal painting, marine grade coating, estate painting, coastal property painting, Cape Town painters');
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/atlantic-beach-estate`);
    }
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Atlantic Beach Estate Painters - Professional Painting Services",
      "description": "Professional painting services for Atlantic Beach Estate luxury coastal properties. Expert interior, exterior, and marine-grade coatings.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/atlantic-beach-estate`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Atlantic Beach Estate",
        "description": "Luxury painting services for Atlantic Beach Estate's exclusive coastal properties with marine-grade protection.",
        "areaServed": "Atlantic Beach Estate, Cape Town",
        "serviceType": "Luxury Coastal Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      // Cleanup
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Atlantic Beach Estate')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Luxury Home Painting',
      description: 'Premium interior and exterior painting for Atlantic Beach Estate\'s prestigious properties with high-end finishes and attention to detail.',
      price: 'From R15,000',
      features: ['Premium paint brands', 'Color consultation', 'Surface preparation', 'Quality guarantee']
    },
    {
      icon: 'ri-brush-line',
      title: 'Coastal Protection',
      description: 'Specialized marine-grade paints and protective coatings designed to withstand Atlantic Beach Estate\'s harsh coastal environment.',
      price: 'From R12,000',
      features: ['Salt-resistant coatings', 'UV protection', 'Moisture barriers', 'Long-lasting finish']
    },
    {
      icon: 'ri-palette-line',
      title: 'Color Consultation',
      description: 'Expert color matching and design consultation services tailored to Atlantic Beach Estate\'s sophisticated aesthetic requirements.',
      price: 'From R2,500',
      features: ['Professional color advice', 'Trend analysis', 'Sample testing', 'Design coordination']
    },
    {
      icon: 'ri-building-line',
      title: 'Estate Maintenance',
      description: 'Comprehensive maintenance programs for Atlantic Beach Estate properties including regular touch-ups and protective treatments.',
      price: 'From R8,000',
      features: ['Scheduled maintenance', 'Preventive care', 'Emergency repairs', 'Property inspections']
    },
    {
      icon: 'ri-shield-line',
      title: 'Waterproofing Services',
      description: 'Advanced waterproofing solutions essential for Atlantic Beach Estate properties exposed to ocean spray and moisture.',
      price: 'From R10,000',
      features: ['Membrane application', 'Crack sealing', 'Moisture testing', 'Warranty coverage']
    },
    {
      icon: 'ri-star-line',
      title: 'Decorative Finishes',
      description: 'Luxury decorative painting techniques including faux finishes, textures, and specialty coatings for discerning homeowners.',
      price: 'From R5,000',
      features: ['Faux techniques', 'Texture application', 'Metallic finishes', 'Custom designs']
    }
  ];

  const testimonials = [
    {
      name: 'David Thompson',
      property: 'Oceanview Villa',
      rating: 5,
      text: 'Outstanding work on our beachfront property. The team understood the unique challenges of coastal painting and delivered exceptional results that have lasted beautifully.',
      image: 'https://public.readdy.ai/ai/img_res/ecbf477e040786d7cc248a9e2be4cfe7.jpg'
    },
    {
      name: 'Jennifer Adams',
      property: 'Modern Estate Home',
      rating: 5,
      text: 'Exceptional attention to detail and quality. Our home looks stunning and the paint finish is flawless. The color consultation was invaluable for achieving our vision.',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20woman%20homeowner%20in%20modern%20Atlantic%20Beach%20Estate%20home%20interior%20with%20Atlantic%20Ocean%20views%20through%20windows%2C%20professionally%20painted%20walls%2C%20luxury%20decor%2C%20satisfied%20client&width=80&height=80&seq=atlantic2new&orientation=squarish'
    },
    {
      name: 'Michael Roberts',
      property: 'Coastal Mansion',
      rating: 5,
      text: 'The marine-grade coating has protected our property perfectly against the salt air. Professional service from start to finish with excellent project management.',
      image: 'https://readdy.ai/api/search-image?query=Distinguished%20man%20homeowner%20at%20Atlantic%20Beach%20Estate%20mansion%20with%20Atlantic%20Ocean%20backdrop%2C%20professionally%20painted%20exterior%2C%20coastal%20luxury%20property%2C%20satisfied%20customer&width=80&height=80&seq=atlantic5new&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Beachfront Villa Renovation',
      description: 'Complete exterior and interior painting with marine-grade protection, custom color scheme, and luxury finishes',
      image: 'https://readdy.ai/api/search-image?query=Luxury%20beachfront%20villa%20in%20Atlantic%20Beach%20Estate%20with%20Atlantic%20Ocean%20views%2C%20freshly%20painted%20white%20exterior%2C%20modern%20architecture%2C%20professional%20painting%20work%2C%20pristine%20finish&width=400&height=300&seq=atlantic3new&orientation=landscape'
    },
    {
      title: 'Contemporary Family Home',
      description: 'Modern color scheme with premium interior finishes, decorative accents, and protective exterior coatings',
      image: 'https://readdy.ai/api/search-image?query=Modern%20family%20home%20in%20Atlantic%20Beach%20Estate%20with%20Atlantic%20Ocean%20setting%2C%20contemporary%20painted%20exterior%20in%20neutral%20tones%2C%20landscaped%20garden%2C%20professional%20residential%20painting&width=400&height=300&seq=atlantic4new&orientation=landscape'
    },
    {
      title: 'Estate Complex Maintenance',
      description: 'Comprehensive maintenance program for multiple properties including waterproofing and protective treatments',
      image: 'https://readdy.ai/api/search-image?query=Atlantic%20Beach%20Estate%20residential%20complex%20with%20Atlantic%20Ocean%20views%2C%20freshly%20maintained%20painted%20exteriors%2C%20multiple%20luxury%20homes%2C%20professional%20maintenance%20work&width=400&height=300&seq=atlantic6new&orientation=landscape'
    },
    {
      title: 'Luxury Interior Transformation',
      description: 'High-end interior painting with decorative finishes, custom textures, and premium materials throughout',
      image: 'https://readdy.ai/api/search-image?query=Luxury%20interior%20of%20Atlantic%20Beach%20Estate%20home%20with%20Atlantic%20Ocean%20views%2C%20professionally%20painted%20walls%2C%20decorative%20finishes%2C%20elegant%20decor%2C%20high-end%20residential%20painting&width=400&height=300&seq=atlantic7new&orientation=landscape'
    }
  ];

  const paintingProcess = [
    {
      step: '1',
      title: 'Property Assessment',
      description: 'Comprehensive evaluation of your Atlantic Beach Estate property including coastal exposure analysis and surface condition assessment.'
    },
    {
      step: '2',
      title: 'Custom Planning',
      description: 'Development of tailored painting solutions considering marine environment factors, architectural style, and luxury standards.'
    },
    {
      step: '3',
      title: 'Surface Preparation',
      description: 'Meticulous preparation including cleaning, sanding, priming, and repair work to ensure optimal paint adhesion and longevity.'
    },
    {
      step: '4',
      title: 'Premium Application',
      description: 'Professional application of marine-grade paints using advanced techniques and equipment for superior finish quality.'
    },
    {
      step: '5',
      title: 'Quality Inspection',
      description: 'Thorough quality control inspection and final touch-ups to ensure every detail meets our exacting standards.'
    },
    {
      step: '6',
      title: 'Maintenance Plan',
      description: 'Comprehensive maintenance recommendations and scheduling to protect your investment in Atlantic Beach Estate\'s coastal environment.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20luxury%20home%20in%20Atlantic%20Beach%20Estate%20with%20Atlantic%20Ocean%20backdrop%2C%20exclusive%20coastal%20estate%2C%20painting%20equipment%2C%20scaffolding%2C%20beautiful%20beachfront%20property%2C%20sunny%20day&width=1920&height=1080&seq=atlantichero2&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Atlantic Beach Estate
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for Atlantic Beach Estate's luxury properties. Specialized coastal protection and premium finishes for your prestigious home with marine-grade materials designed to withstand the harsh coastal environment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Atlantic Beach Estate Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Painting Excellence in Atlantic Beach Estate
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Atlantic Beach Estate represents the pinnacle of coastal luxury living in Cape Town, and your property deserves painting services that match this prestigious standard. Our specialized team understands the unique challenges posed by the Atlantic Ocean's proximity, including salt air corrosion, strong coastal winds, and intense UV exposure.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Located along one of Cape Town's most exclusive coastlines, Atlantic Beach Estate properties require more than standard painting solutions. We provide comprehensive painting services using marine-grade materials specifically formulated to protect against the harsh coastal environment while maintaining the aesthetic excellence expected in this premier location.
              </p>
              <p className="text-lg text-gray-600">
                Our experience with Atlantic Beach Estate's architectural styles, from contemporary glass structures to classic coastal designs, ensures we deliver results that enhance your property's value while providing long-lasting protection against the elements.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Atlantic%20Beach%20Estate%20luxury%20coastal%20homes%20with%20Atlantic%20Ocean%20views%2C%20exclusive%20beachfront%20properties%2C%20modern%20architecture%2C%20pristine%20exteriors%2C%20Atlantic%20Beach%20Estate%20scenery&width=600&height=400&seq=atlantic8new&orientation=landscape"
                alt="Atlantic Beach Estate Properties"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Local Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Atlantic Beach Estate Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions designed specifically for Atlantic Beach Estate's unique coastal environment and luxury property requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-black mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Atlantic Beach Estate Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring exceptional results for your luxury coastal property.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us for Atlantic Beach Estate */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Us for Atlantic Beach Estate?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Unmatched expertise in luxury coastal property painting with specialized knowledge of Atlantic Beach Estate's unique requirements.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-home-4-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Local Expertise</h3>
              <p className="text-gray-600">Deep knowledge of Atlantic Beach Estate's unique requirements, architectural styles, and coastal conditions gained through years of specialized experience.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-building-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Marine Grade Materials</h3>
              <p className="text-gray-600">Specialized paints and coatings designed specifically for coastal environments, providing superior protection against salt air and moisture damage.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-community-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Luxury Standards</h3>
              <p className="text-gray-600">Premium service and finishes that match the prestige of Atlantic Beach Estate properties, with attention to every detail and luxury expectations.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Flexible Scheduling</h3>
              <p className="text-gray-600">Work around your schedule with minimal disruption to your Atlantic Beach Estate lifestyle, including weekend and holiday availability when needed.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Atlantic Beach Estate Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our expertise in luxury coastal property painting with recent successful projects in Atlantic Beach Estate.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Atlantic Beach Estate Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied Atlantic Beach Estate property owners who have experienced our exceptional painting services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coastal Challenges Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Understanding Atlantic Beach Estate's Coastal Challenges
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Environmental Factors We Address</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-drop-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Salt Air Corrosion</h4>
                    <p className="text-gray-600">Constant exposure to salt-laden air requires specialized protective coatings to prevent premature paint failure and surface degradation.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-sun-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">UV Radiation</h4>
                    <p className="text-gray-600">Intense coastal sunlight demands UV-resistant formulations to maintain color integrity and prevent chalking or fading over time.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-windy-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Coastal Winds</h4>
                    <p className="text-gray-600">Strong Atlantic winds carry abrasive particles that can damage standard paint finishes, requiring enhanced durability solutions.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-water-percent-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Moisture Management</h4>
                    <p className="text-gray-600">High humidity and occasional ocean spray require moisture-resistant barriers to prevent mold, mildew, and structural damage.</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Atlantic%20Beach%20Estate%20coastal%20environment%20with%20Atlantic%20Ocean%20waves%2C%20salt%20air%20effects%2C%20coastal%20winds%2C%20UV%20exposure%20on%20luxury%20beachfront%20properties%2C%20environmental%20challenges&width=600&height=500&seq=atlantic9new&orientation=portrait"
                alt="Coastal Environmental Challenges"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Paint Your Atlantic Beach Estate Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town's premier painting specialists for Atlantic Beach Estate. Our expert team is ready to provide you with a comprehensive consultation and detailed quote for your luxury coastal property. Experience the difference that specialized coastal painting expertise makes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Additional Contact Option */}
      <div className="text-center py-8 bg-white">
      </div>

      <Footer />
      
      {/* Elfsight Popup */}
      <div className="elfsight-app-a8d09b4b-1f9c-47af-9c95-257e6e1aa6a7" data-elfsight-app-lazy></div>
    </div>
  );
}
